// src/pages/reservas/components/ModalPrepago.jsx
import React, { useMemo } from 'react';

export default function ModalPrepago({
  open,
  tituloZona,
  pisoZona,
  eventoActual,
  error,
  form,
  setForm,
  loadingPaquetes,
  opcionesPago,
  generandoEntrega,
  onClose,
  onComprar,
  cambiaPropietario = false,

  // ✅ carrito extras
  combosEntradas,
  carrito,
  setCarrito,
  totalExtras,
  totalGeneral,
}) {
  // ✅ Hooks SIEMPRE arriba (sin condicionales)
  const paquetes = useMemo(() => (Array.isArray(opcionesPago) ? opcionesPago : []), [opcionesPago]);

  const selectedId = form?.paqueteId || '';
  const selectedPkg = useMemo(
    () => paquetes.find((p) => p.id === selectedId) || null,
    [paquetes, selectedId]
  );

  const fmtMoney = (n) => `${(Number(n || 0) / 100).toFixed(2)} Bs`;

  const incluyeToList = (incluye) => {
    if (Array.isArray(incluye)) return incluye.filter(Boolean).map(String);
    if (typeof incluye === 'string') return incluye.split(/\r?\n|,/).map((x) => x.trim()).filter(Boolean);
    return [];
  };

  const incQty = (id, delta) => {
    if (!setCarrito) return;
    setCarrito((prev) => {
      const cur = Number(prev?.[id] || 0);
      const next = cur + delta;
      if (next <= 0) {
        const copy = { ...(prev || {}) };
        delete copy[id];
        return copy;
      }
      return { ...(prev || {}), [id]: next };
    });
  };

  const combos = useMemo(() => (Array.isArray(combosEntradas) ? combosEntradas : []), [combosEntradas]);

  // ✅ Render
  if (!open) return null;

  const overlay = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 18,
    zIndex: 9999,
  };

  const card = {
    width: 'min(720px, 96vw)',
    maxHeight: '92vh',
    overflow: 'auto',
    borderRadius: 18,
    background: 'rgba(10,14,20,0.96)',
    border: '1px solid rgba(255,255,255,0.10)',
    boxShadow: '0 20px 60px rgba(0,0,0,0.55)',
  };

  const header = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
  };

  const closeBtn = {
    width: 34,
    height: 34,
    borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.12)',
    background: 'transparent',
    color: '#e5e7eb',
    cursor: 'pointer',
    fontSize: 18,
    lineHeight: '32px',
  };

  const section = { padding: '14px 16px' };

  const muted = { opacity: 0.85, color: '#cbd5e1' };

  const infoBox = {
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 14,
    padding: 12,
    background: 'rgba(255,255,255,0.03)',
  };

  const titleLine = { fontWeight: 900, color: '#e5e7eb', textTransform: 'uppercase' };

  const pkgCard = (active) => ({
    border: active ? '1px solid rgba(16,185,129,0.75)' : '1px solid rgba(255,255,255,0.10)',
    background: active ? 'rgba(16,185,129,0.08)' : 'rgba(255,255,255,0.03)',
    borderRadius: 14,
    padding: 12,
    cursor: 'pointer',
  });

  const pill = {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 999,
    border: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.04)',
    fontSize: 12,
    opacity: 0.9,
  };

  const footer = {
    padding: 16,
    display: 'flex',
    gap: 10,
    justifyContent: 'flex-end',
    borderTop: '1px solid rgba(255,255,255,0.08)',
    position: 'sticky',
    bottom: 0,
    background: 'rgba(10,14,20,0.96)',
  };

  const btn = (primary) => ({
    borderRadius: 999,
    padding: '10px 16px',
    border: primary ? 'none' : '1px solid rgba(255,255,255,0.14)',
    background: primary ? '#19d38c' : 'rgba(255,255,255,0.06)',
    color: primary ? '#06100c' : '#e5e7eb',
    fontWeight: 900,
    cursor: 'pointer',
    minWidth: 160,
    opacity: generandoEntrega ? 0.7 : 1,
  });

  const canBuy = !!selectedId && !loadingPaquetes && !generandoEntrega;

  return (
    <div style={overlay} onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <div style={card}>
        <div style={header}>
          <div style={{ fontWeight: 900, color: '#e5e7eb' }}>Prepago</div>
          <button type="button" style={closeBtn} onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </div>

        <div style={section}>
          <div style={titleLine}>{tituloZona}</div>
          <div style={{ ...muted, marginTop: 4 }}>
            {pisoZona}
            {eventoActual?.fechaTexto ? ` · ${eventoActual.fechaTexto}` : ''}
          </div>

          <div style={{ marginTop: 12, ...infoBox }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <div style={muted}>
                <div style={{ fontSize: 12 }}>Cliente:</div>
                <div style={{ fontSize: 12, marginTop: 6 }}>RRPP/Anfitrión:</div>
              </div>
              <div style={{ textAlign: 'right', color: '#e5e7eb', fontWeight: 800 }}>
                <div>{(form?.nombreCliente || '').trim() || '—'}</div>
                <div style={{ marginTop: 6 }}>{(form?.atribuidoANombre || '').trim() || '—'}</div>
              </div>
            </div>
          </div>

          {error ? (
            <div style={{ marginTop: 12, ...infoBox, border: '1px solid rgba(239,68,68,0.45)', background: 'rgba(239,68,68,0.10)', color: '#fecaca', fontWeight: 800 }}>
              {error}
            </div>
          ) : null}

          {cambiaPropietario && <p className="reb-warning">Al confirmar el prepago, la reserva pasará a ser tuya. Se conserva el cliente y el historial; las entradas quedarán disponibles para ti y Owner.</p>}
          <div style={{ marginTop: 14, fontWeight: 900, color: '#e5e7eb' }}>Selecciona un paquete</div>

          {loadingPaquetes ? (
            <div style={{ ...muted, marginTop: 10 }}>Cargando paquetes...</div>
          ) : paquetes.length === 0 ? (
            <div style={{ ...muted, marginTop: 10 }}>No hay paquetes disponibles para esta zona.</div>
          ) : (
            <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
              {paquetes.map((p) => {
                const active = p.id === selectedId;
                const incluye = incluyeToList(p.incluye);
                const entradas = Number(p.cantidadEntradas || 0);
                const consumo = Number.isSafeInteger(p.consumoCentavos) && p.consumoCentavos >= 0 ? p.consumoCentavos : null;

                return (
                  <div
                    key={p.id}
                    style={pkgCard(active)}
                    onClick={() => setForm((f) => ({ ...f, paqueteId: p.id }))}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontWeight: 900, color: '#e5e7eb', textTransform: 'uppercase' }}>
                          {p.nombre || 'PAQUETE'}
                        </div>
                        <div style={{ marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <span className="prepago-package-consumo">Consumo: <strong>{consumo === null ? 'No especificado' : fmtMoney(consumo)}</strong></span>
                          <span style={pill}>{entradas} {entradas === 1 ? 'entrada incluida' : 'entradas incluidas'}</span>
                        </div>
                      </div>
                      <div className="prepago-package-price" style={{ fontWeight: 900, color: '#a7f3d0' }}>{fmtMoney(p.precioCentavos)}</div>
                    </div>

                    {/* ✅ Detalle SIEMPRE visible (tipo carrito) */}
                    <div style={{ marginTop: 10, borderTop: '1px dashed rgba(255,255,255,0.12)', paddingTop: 10 }}>
                      <div style={{ fontWeight: 900, color: '#e5e7eb', marginBottom: 6 }}>Incluye</div>
                      {incluye.length > 0 ? (
                        <ul style={{ margin: '6px 0 0 18px', color: '#dbeafe' }}>
                          {incluye.map((x, i) => (
                            <li key={`${p.id}-inc-${i}`} style={{ marginBottom: 4 }}>
                              {x}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div style={muted}>Sin detalle</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ✅ Carrito de entradas extra */}
          <div style={{ marginTop: 16, fontWeight: 900, color: '#e5e7eb' }}>Añadir entradas extra</div>

          {combos.length === 0 ? (
            <div style={{ ...muted, marginTop: 10 }}>Este evento no tiene combos configurados.</div>
          ) : (
            <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
              {combos.map((c) => {
                const qty = Number(carrito?.[c.id] || 0);
                const incluye = incluyeToList(c.incluye);

                return (
                  <div
                    key={c.id}
                    style={{
                      border: qty > 0 ? '1px solid rgba(16,185,129,0.75)' : '1px solid rgba(255,255,255,0.10)',
                      background: qty > 0 ? 'rgba(16,185,129,0.08)' : 'rgba(255,255,255,0.03)',
                      borderRadius: 14,
                      padding: 12,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: 900, color: '#e5e7eb', textTransform: 'uppercase' }}>{c.nombre}</div>
                        <div style={{ ...muted, marginTop: 4 }}>
                          {Number(c.cantidadEntradas || 1)} entrada{Number(c.cantidadEntradas || 1) !== 1 ? 's' : ''} · {fmtMoney(c.precioCentavos)}
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <button
                          type="button"
                          onClick={() => incQty(c.id, -1)}
                          style={{
                            width: 34,
                            height: 34,
                            borderRadius: 10,
                            border: '1px solid rgba(255,255,255,0.14)',
                            background: 'rgba(255,255,255,0.06)',
                            color: '#e5e7eb',
                            cursor: 'pointer',
                            fontWeight: 900,
                          }}
                        >
                          -
                        </button>
                        <div style={{ minWidth: 22, textAlign: 'center', fontWeight: 900, color: '#e5e7eb' }}>{qty}</div>
                        <button
                          type="button"
                          onClick={() => incQty(c.id, 1)}
                          style={{
                            width: 34,
                            height: 34,
                            borderRadius: 10,
                            border: '1px solid rgba(16,185,129,0.55)',
                            background: 'rgba(16,185,129,0.12)',
                            color: '#a7f3d0',
                            cursor: 'pointer',
                            fontWeight: 900,
                          }}
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <div style={{ marginTop: 10, borderTop: '1px dashed rgba(255,255,255,0.12)', paddingTop: 10 }}>
                      <div style={{ fontWeight: 900, color: '#e5e7eb', marginBottom: 6 }}>Incluye</div>
                      {incluye.length > 0 ? (
                        <ul style={{ margin: '6px 0 0 18px', color: '#dbeafe' }}>
                          {incluye.map((x, i) => (
                            <li key={`${c.id}-inc-${i}`} style={{ marginBottom: 4 }}>
                              {x}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div style={muted}>Sin detalle</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Totales */}
          <div style={{ marginTop: 14, ...infoBox }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, color: '#e5e7eb' }}>
              <span style={muted}>Total extras:</span>
              <b>{fmtMoney(totalExtras)}</b>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, color: '#e5e7eb', marginTop: 6 }}>
              <span>Total general:</span>
              <b>{fmtMoney(totalGeneral)}</b>
            </div>
          </div>

          {!selectedPkg ? (
            <div style={{ ...muted, marginTop: 10 }}>
              Selecciona un paquete para habilitar <b>Confirmar cobro y prepago</b>.
            </div>
          ) : null}
        </div>

        <div style={footer}>
          <button type="button" style={btn(false)} onClick={onClose}>
            Cerrar
          </button>
          <button
            type="button"
            style={btn(true)}
            onClick={onComprar}
            disabled={!canBuy}
            title={!selectedId ? 'Selecciona un paquete' : ''}
          >
            Comprar / Confirmar
          </button>
        </div>
      </div>
    </div>
  );
}
